/**
 * Action bar.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* ── Action bar ───────────────────────────────────────────────────────────
   One button per gameplay window, docked in the bottom-left corner (moved out
   of the player frame 2026-10-16, on request). See ui/action-bar.ts for why
   tutorial mode gets its own layout rather than showing four floating
   tooltips on top of each other. */
/* The dock: the row of squares, then the long World Chat button under it.
   1002 is the layer the player frame's blocks use, and it drops to 1000 under
   an open window's scrim the same way (.bv-ab-dock-under-panel, toggled from
   the frame loop beside .bv-hud-under-panel). The dock is pointer-events:none
   so the gaps between buttons still reach the world. Safe-area insets keep it
   off a notch or a rounded corner in landscape. */
.bv-ab-dock {
  position: absolute; z-index: 1002;
  left: calc(4px + env(safe-area-inset-left, 0px));
  bottom: calc(4px + env(safe-area-inset-bottom, 0px));
  display: flex; flex-direction: column; gap: 8px;
  max-width: calc(100vw - 8px); pointer-events: none;
  /* The shade. Janeks, 2026-10-16: the button art has a transparent margin and
     a black rim, so over bright ground the captions under the squares had
     nothing to read against. A dark backing behind the whole dock, feathered
     by a wide same-colour shadow so it reads as shade on the ground rather
     than as another panel. The 8px padding is why left/bottom are 4px: the
     buttons land where they were before. */
  padding: 8px;
  background: rgba(10,6,3,0.78);
  border-radius: 14px;
  box-shadow: 0 0 20px 10px rgba(10,6,3,0.7);
}
.bv-ab-dock-under-panel { z-index: 1000; }
/* No top border and no margin any more: the bar is its own element under the
   two panels (2026-09-15), not a section inside one, so a divider would be a
   line drawn across nothing. */
/* gap 8 and space-between agree exactly at the .bv-hud width derived in
   hud.css.ts (7x44 + 6x8 = 356). They are both here on purpose: the gap is
   what spaces a row that has wrapped, space-between is what keeps the row
   flush to both edges of the frame if the column is ever widened past the
   sum, and wrap is what turns "one button too many" into a visible second
   row instead of squares spilling out of the player card. */
.bv-ab { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; }
.bv-ab-cell { position: relative; display: flex; flex-direction: column; align-items: center; }
.bv-ab-btn {
  /* Opt back in - see .bv-hud-mini-btn above. */
  pointer-events: auto;
  position: relative; width: 44px; height: 44px; flex: 0 0 auto; padding: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--brass-bright); cursor: pointer;
  background: linear-gradient(170deg, var(--panel-a), var(--well));
  /* The "stylistic border": a brass hairline, an inner bevel, and a drop
     shadow — the same three-part treatment .bv-hud itself uses, scaled down. */
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.07), inset 0 0 8px rgba(0,0,0,0.5),
              0 2px 5px rgba(0,0,0,0.45);
  transition: color 0.12s ease, border-color 0.12s ease, transform 0.08s ease;
}
.bv-ab-btn svg { width: 22px; height: 22px; display: block; }
.bv-ab-btn:hover:not(:disabled) { color: var(--brass-hot); border-color: var(--brass-hot); }
.bv-ab-btn:active:not(:disabled) { transform: translateY(1px); }
/* Pressed = this window is open, so the button reads as a toggle that is on. */
.bv-ab-btn-active {
  color: var(--brass-hot); border-color: var(--brass-hot);
  background: linear-gradient(170deg, #3a2717, var(--panel-a));
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 10px rgba(255,189,92,0.18),
              0 0 0 1px rgba(255,189,92,0.18);
}
.bv-ab-btn:disabled { color: var(--bone-faint); border-color: var(--line); cursor: default; opacity: 0.5; }
/* Keycap, bottom-right, the same corner WoW puts its binding text in. */
.bv-ab-key {
  position: absolute; right: 2px; bottom: 1px;
  font: 700 9px var(--font-technical); letter-spacing: 0.04em;
  color: var(--bone-dim); text-shadow: 0 1px 2px rgba(0,0,0,0.9);
}
.bv-ab-btn-active .bv-ab-key { color: var(--brass-hot); }

/* Tooltip. Floating (absolute, no layout cost) while tutorial mode is off,
   so hovering never nudges the panel below it. */
.bv-ab-tip {
  /* ABOVE the button: the bar sits on the bottom edge of the screen, and a
     tooltip hung below it would be drawn off-screen. */
  position: absolute; bottom: calc(100% + 7px); left: 50%; transform: translateX(-50%);
  z-index: 2; white-space: nowrap; pointer-events: none; opacity: 0;
  padding: 3px 7px; border-radius: var(--radius-tape);
  background: rgba(15,9,5,0.94); border: 1px solid var(--line);
  color: var(--bone); font: 10px var(--font-technical); letter-spacing: 0.04em;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  transition: opacity 0.12s ease;
}
.bv-ab-cell:hover .bv-ab-tip { opacity: 1; }

/* Tutorial mode: every caption is shown at once, which four floating tooltips
   cannot do without stacking. They move into normal flow and the cells widen
   to fit, so the bar keeps a stable height instead of growing on hover. */
.bv-ab-teaching { flex-wrap: wrap; }
.bv-ab-teaching .bv-ab-cell { flex: 1 1 0; min-width: 0; }
.bv-ab-teaching .bv-ab-tip {
  position: static; transform: none; opacity: 1; margin-top: 4px;
  max-width: 100%; white-space: normal; text-align: center;
  background: none; border: none; box-shadow: none; padding: 0;
  color: var(--bone); font-size: 9px; line-height: 1.25;
  text-shadow: 0 1px 2px #000, 0 0 4px rgba(0,0,0,0.9);
}

/* ── Button art (Higgsfield), 2026-10-14 ──────────────────────────────────
   Each button is a real riveted brass plate with a real object on it, standing
   proud of the panel: a hard lower edge for thickness, a soft cast shadow, a
   lift on hover and a press-in when its window is open. The plate URL arrives
   as --hud-btn (hud.ts). */
.bv-ab-btn {
  width: 46px; height: 46px; border: 0; border-radius: 9px; color: var(--brass-bright);
  background: var(--hud-btn, linear-gradient(170deg, var(--panel-a), var(--well))) center / 100% 100% no-repeat;
  box-shadow: 0 3px 0 #0b0704, 0 6px 9px rgba(0,0,0,0.55);
  transition: transform 0.14s cubic-bezier(0.3, 1.5, 0.5, 1), box-shadow 0.14s ease, filter 0.14s ease;
}
.bv-ab-art {
  position: relative; width: 31px; height: 31px; display: block; pointer-events: none;
  object-fit: contain; filter: drop-shadow(0 2px 2px rgba(0,0,0,0.8));
  transition: transform 0.22s cubic-bezier(0.3, 1.6, 0.5, 1);
}
.bv-ab-btn:hover:not(:disabled) {
  transform: translateY(-2px); border-color: transparent; filter: brightness(1.12) saturate(1.08);
  box-shadow: 0 5px 0 #0b0704, 0 9px 12px rgba(0,0,0,0.5), 0 0 12px rgba(255,189,92,0.22);
}
.bv-ab-btn:hover:not(:disabled) .bv-ab-art { transform: scale(1.14) rotate(-5deg); }
.bv-ab-btn:active:not(:disabled) { transform: translateY(2px); box-shadow: 0 1px 0 #0b0704, 0 2px 4px rgba(0,0,0,0.6); }
/* Open window = the button is held down and lit from inside. */
.bv-ab-btn-active, .bv-ab-btn-active:hover:not(:disabled) {
  transform: translateY(2px); border-color: transparent; filter: brightness(1.2) sepia(0.25);
  background-color: transparent;
  box-shadow: 0 1px 0 #0b0704, 0 0 0 1px rgba(255,189,92,0.5), 0 0 14px rgba(255,189,92,0.4);
}
.bv-ab-btn:disabled { filter: grayscale(0.85) brightness(0.65); opacity: 0.8; border-color: transparent; }
.bv-ab-key {
  right: 4px; bottom: 3px; z-index: 1; padding: 0 2px; border-radius: 2px;
  background: rgba(10,6,3,0.72); color: var(--bone);
}
@media (prefers-reduced-motion: reduce) {
  .bv-ab-btn, .bv-ab-art { transition: none; }
  .bv-ab-btn:hover:not(:disabled) .bv-ab-art { transform: none; }
}

/* ── The long World Chat button ───────────────────────────────────────────
   Janeks: "Make the world chat not a menu button but a elongated world chat
   UI button." Full width of the dock, under the squares. The player card's
   plate art (--hud-plate) rather than the square button plate: the square one
   is a single riveted tile, and stretched to 8:1 its rivets turn into
   smears. The card plate is already a wide panel and is cropped, not
   stretched (cover). */
.bv-ab-wide {
  pointer-events: auto; position: relative;
  display: flex; align-items: center; gap: 8px;
  height: 34px; padding: 0 10px 0 8px; cursor: pointer;
  color: var(--bone); font: 700 12px/1 var(--font-display);
  letter-spacing: 0.14em; text-transform: uppercase; text-align: left;
  text-shadow: 0 1px 2px rgba(0,0,0,0.9);
  background: var(--hud-plate, linear-gradient(165deg, rgba(42,33,22,0.92), rgba(26,20,16,0.9))) center / cover no-repeat;
  border: 1px solid var(--line); border-radius: 9px;
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.1), 0 3px 0 #0b0704, 0 6px 9px rgba(0,0,0,0.55);
  transition: transform 0.14s cubic-bezier(0.3, 1.5, 0.5, 1), box-shadow 0.14s ease, filter 0.14s ease, border-color 0.12s ease;
}
.bv-ab-wide .bv-ab-art { width: 24px; height: 24px; flex: 0 0 auto; }
.bv-ab-wide svg { width: 20px; height: 20px; flex: 0 0 auto; color: var(--brass-bright); }
.bv-ab-wide-label { flex: 0 1 auto; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* The keycap sits right after the label (2026-10-19), not pinned to the far
   edge where it read as a separate control. In flow, so a long label
   ellipsizes before it and the badge never overlaps the text. */
.bv-ab-wide .bv-ab-key { position: static; flex: 0 0 auto; margin-left: -2px; }
/* Invisible tap-area growth: the plate is 30-40px tall, the target is 44. */
.bv-ab-wide::after { content: ""; position: absolute; inset: -7px 0; }
.bv-ab-wide:hover:not(:disabled) {
  transform: translateY(-2px); border-color: var(--brass-hot); filter: brightness(1.12);
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.1), 0 5px 0 #0b0704, 0 9px 12px rgba(0,0,0,0.5), 0 0 12px rgba(255,189,92,0.22);
}
.bv-ab-wide:hover:not(:disabled) .bv-ab-art { transform: scale(1.12) rotate(-5deg); }
.bv-ab-wide:active:not(:disabled) { transform: translateY(2px); box-shadow: 0 1px 0 #0b0704, 0 2px 4px rgba(0,0,0,0.6); }
/* Chat open = held down and lit, same language as an open square. */
.bv-ab-wide.bv-ab-btn-active {
  transform: translateY(2px); color: var(--brass-hot); border-color: var(--brass-hot);
  filter: brightness(1.15) sepia(0.2);
  box-shadow: 0 1px 0 #0b0704, 0 0 0 1px rgba(255,189,92,0.5), 0 0 14px rgba(255,189,92,0.4);
}
.bv-ab-wide:disabled { filter: grayscale(0.85) brightness(0.65); opacity: 0.8; cursor: default; }

/* Phones: the six squares must fit a 320px screen with its 12px gutters
   (6 x 40 + 5 x 6 = 270, inside 296) — action-bar.test.ts checks this sum. */
@media (max-width: 480px) {
  .bv-ab-dock { gap: 6px; }
  .bv-ab { gap: 6px; }
  .bv-ab-btn { width: 40px; height: 40px; }
  .bv-ab-art { width: 27px; height: 27px; }
  .bv-ab-wide { height: 30px; font-size: 11px; }
}
/* Landscape phones (about 375px tall): stacked, the dock is ~114px and runs
   up into the player card's Load panel. One row instead — World Chat beside
   the squares, as tall as they are — halves it. The phone-size squares too,
   so the row (6 x 40 + 5 x 6 + 6 + 150 = 426px) clears the compact minimap
   and gait indicator on a 667px-wide screen. Measured at 812 and 667 x 375. */
@media (max-height: 480px) and (min-width: 561px) {
  .bv-ab-dock { flex-direction: row; align-items: flex-start; gap: 6px; max-width: none; }
  .bv-ab { flex-wrap: nowrap; gap: 6px; }
  .bv-ab-btn { width: 40px; height: 40px; }
  .bv-ab-art { width: 27px; height: 27px; }
  .bv-ab-wide { width: 150px; height: 40px; font-size: 11px; }
}
@media (prefers-reduced-motion: reduce) {
  .bv-ab-wide, .bv-ab-wide .bv-ab-art { transition: none; }
  .bv-ab-wide:hover:not(:disabled) .bv-ab-art { transform: none; }
}
`;
